/**
 * =========================================================
 * Social Media Hub - Premium Sidenav Collapse Styles
 * =========================================================
 *
 * Modern, sleek navigation item styles with proper contrast,
 * smooth animations, and premium visual aesthetics.
 */

function collapseItem(theme, ownerState) {
  const { palette, transitions, breakpoints, boxShadows, borders, functions } = theme;
  const { active, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = ownerState;

  const { white, transparent, dark, grey, gradients, primary } = palette;
  const { md } = boxShadows;
  const { borderRadius } = borders;
  const { pxToRem, rgba, linearGradient } = functions;

  // Determine background and text colors based on state
  const getBackgroundColor = () => {
    if (active) {
      return linearGradient(
        gradients[sidenavColor]?.main || gradients.primary.main,
        gradients[sidenavColor]?.state || gradients.primary.state
      );
    }
    return transparent.main;
  };

  const getTextColor = () => {
    // Always use dark text for white/light sidenav
    if (whiteSidenav && !darkMode) {
      return active ? white.main : dark.main;
    }
    // For dark mode or colored sidenav
    if (darkMode) {
      return active ? white.main : grey[300];
    }
    // Default: white sidenav with dark text
    return active ? white.main : dark.main;
  };

  return {
    background: getBackgroundColor(),
    color: getTextColor(),
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: `${pxToRem(10)} ${pxToRem(12)}`,
    margin: `${pxToRem(2)} ${pxToRem(12)}`,
    borderRadius: "12px",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    boxShadow:
      active && !whiteSidenav && !darkMode && !transparentSidenav
        ? "0 4px 14px rgba(99, 102, 241, 0.25), 0 2px 6px rgba(99, 102, 241, 0.15)"
        : "none",
    position: "relative",
    overflow: "hidden",
    [breakpoints.up("xl")]: {
      transition: transitions.create(["box-shadow", "background-color", "transform"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter,
      }),
    },

    // Premium hover effect
    "&:hover, &:focus": {
      backgroundColor: () => {
        if (active) return null; // Keep gradient for active items
        return whiteSidenav || !darkMode ? grey[100] : rgba(white.main, 0.1);
      },
      transform: "translateX(4px)",
    },

    // Active indicator line
    "&::before": active
      ? {
          content: '""',
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          width: "4px",
          height: "60%",
          background: "white",
          borderRadius: "0 4px 4px 0",
          opacity: 0.8,
        }
      : {},

    // Shimmer effect for active items
    "&::after": active
      ? {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
          animation: "shimmer 2s infinite",
        }
      : {},
  };
}

function collapseIconBox(theme, ownerState) {
  const { palette, transitions, borders, functions } = theme;
  const { transparentSidenav, whiteSidenav, darkMode, active } = ownerState;

  const { white, dark, primary, grey } = palette;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  // Determine icon color based on state
  const getIconColor = () => {
    if (active) return white.main;
    if (whiteSidenav && !darkMode) return dark.main;
    if (darkMode) return grey[300];
    return dark.main;
  };

  return {
    minWidth: pxToRem(36),
    minHeight: pxToRem(36),
    color: getIconColor(),
    borderRadius: "10px",
    display: "grid",
    placeItems: "center",
    transition: transitions.create(["margin", "transform"], {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard,
    }),
    background: active
      ? "rgba(255, 255, 255, 0.15)"
      : whiteSidenav || !darkMode
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.05)",

    "& svg, svg g": {
      color: getIconColor(),
    },

    // Hover scale effect
    "&:hover": {
      transform: "scale(1.05)",
    },
  };
}

const collapseIcon = (
  { palette: { white, gradients, primary, grey, dark } },
  { active, whiteSidenav, darkMode }
) => ({
  color: active
    ? white.main
    : whiteSidenav && !darkMode
    ? dark.main
    : darkMode
    ? grey[300]
    : dark.main,
  fontSize: "1.1rem !important",
  transition: "all 0.2s ease",
});

function collapseText(theme, ownerState) {
  const { typography, transitions, breakpoints, functions } = theme;
  const { miniSidenav, transparentSidenav, active, whiteSidenav, darkMode } = ownerState;

  const { size, fontWeightRegular, fontWeightMedium, fontWeightSemiBold } = typography;
  const { pxToRem } = functions;

  // Determine text color
  const getTextColor = () => {
    if (active) return "inherit"; // Will use parent's color
    if (whiteSidenav && !darkMode) return dark.main;
    if (darkMode) return grey[300];
    return dark.main;
  };

  const { grey, dark } = theme.palette;

  return {
    marginLeft: pxToRem(12),

    [breakpoints.up("xl")]: {
      opacity: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : 1,
      maxWidth: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : "100%",
      marginLeft: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : pxToRem(12),
      transition: transitions.create(["opacity", "margin"], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "& span": {
      fontWeight: active ? fontWeightSemiBold : fontWeightMedium,
      fontSize: "0.9rem",
      lineHeight: 1.5,
      letterSpacing: "-0.01em",
      color: getTextColor(),
    },
  };
}

export { collapseItem, collapseIconBox, collapseIcon, collapseText };
